import type {
  AllocateLicenseKeyRequest,
  LicenseKey,
  LicenseKeyStatus,
  RevokeLicenseKeyRequest,
  UpdateLicenseKeyRequest,
  VoidLicenseKeyRequest,
} from '@techgitter/contracts';

const BASE_URL = '/api/license_keys';

async function readJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      // ignore
    }
    throw new Error(`${op} failed (${response.status} ${response.statusText})${detail}`);
  }
  return (await response.json()) as T;
}

export interface FetchLicenseKeysParams {
  status?: LicenseKeyStatus;
  sku?: string;
  productId?: string;
  customerId?: string;
  jobId?: string;
}

function buildQuery(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) q.set(k, v);
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** List keys with optional filters (newest first). */
export async function fetchLicenseKeys(
  params: FetchLicenseKeysParams = {},
  signal?: AbortSignal,
): Promise<LicenseKey[]> {
  const url = `${BASE_URL}${buildQuery({
    status: params.status,
    sku: params.sku,
    productId: params.productId,
    customerId: params.customerId,
    jobId: params.jobId,
  })}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
  const data = await readJson<{ items: LicenseKey[] }>(response, 'fetchLicenseKeys');
  return data.items ?? [];
}

/** Available-pool keys for a SKU/product — the ticket provisioning picker. */
export async function fetchAvailableLicenseKeys(
  params: { productId?: string; sku?: string },
  signal?: AbortSignal,
): Promise<LicenseKey[]> {
  const url = `${BASE_URL}/available${buildQuery({ productId: params.productId, sku: params.sku })}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
  const data = await readJson<{ items: LicenseKey[] }>(response, 'fetchAvailableLicenseKeys');
  return data.items ?? [];
}

/** Single key with allocation history. */
export async function fetchLicenseKey(keyId: string, signal?: AbortSignal): Promise<LicenseKey> {
  const response = await fetch(`${BASE_URL}/${encodeURIComponent(keyId)}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  return readJson<LicenseKey>(response, 'fetchLicenseKey');
}

export async function allocateLicenseKey(
  keyId: string,
  request: AllocateLicenseKeyRequest,
  signal?: AbortSignal,
): Promise<LicenseKey> {
  const response = await fetch(`${BASE_URL}/${encodeURIComponent(keyId)}/allocate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<LicenseKey>(response, 'allocateLicenseKey');
}

export async function revokeLicenseKey(
  keyId: string,
  request: RevokeLicenseKeyRequest = {},
  signal?: AbortSignal,
): Promise<LicenseKey> {
  const response = await fetch(`${BASE_URL}/${encodeURIComponent(keyId)}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<LicenseKey>(response, 'revokeLicenseKey');
}

export async function voidLicenseKey(
  keyId: string,
  request: VoidLicenseKeyRequest = {},
  signal?: AbortSignal,
): Promise<LicenseKey> {
  const response = await fetch(`${BASE_URL}/${encodeURIComponent(keyId)}/void`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<LicenseKey>(response, 'voidLicenseKey');
}

export async function unvoidLicenseKey(keyId: string, signal?: AbortSignal): Promise<LicenseKey> {
  const response = await fetch(`${BASE_URL}/${encodeURIComponent(keyId)}/unvoid`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    signal,
  });
  return readJson<LicenseKey>(response, 'unvoidLicenseKey');
}

export async function updateLicenseKey(
  keyId: string,
  request: UpdateLicenseKeyRequest,
  signal?: AbortSignal,
): Promise<LicenseKey> {
  const response = await fetch(`${BASE_URL}/${encodeURIComponent(keyId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<LicenseKey>(response, 'updateLicenseKey');
}
